/**
 * For-block widgets:
 * - PrettyForWidget: L0 pretty widget for ::for header line
 * - ForButtonGroupWidget: Run/Stop button matching define's ButtonGroupWidget style
 * - ForEndWidget: L0 pretty widget for ::endfor close line
 */

import { WidgetType } from "@codemirror/view";
import { parseAssetRefPath } from "@magic-md/core";
import { t as translate } from "@magic-md/i18n";
import type { ForExecutionStatus } from "../../../store/plugin-execution-store";
import {
    attachSyntaxEditInteractions,
    createClearExecutionButton,
    createHoverDetail,
    createInlineRenderToggleButton,
    createPrettySyntaxShell,
    createSyntaxBadge,
    createTemplateInputsButton,
    createTextSpan,
    createWatchIndicator,
    shieldGroupEvents,
} from "../shared/widgets";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

/* ── PrettyForWidget — L0 pretty widget for ::for header ── */

export class PrettyForWidget extends WidgetType {
    constructor(
        readonly forId: string,
        readonly each: string,
        readonly ofExpr: string,
        readonly summary: string,
        readonly isTemplate: boolean = false,
        readonly isWatched: boolean = false,
    ) {
        super();
    }
    eq(other: PrettyForWidget) {
        return (
            other.forId === this.forId &&
            other.each === this.each &&
            other.ofExpr === this.ofExpr &&
            other.summary === this.summary &&
            other.isTemplate === this.isTemplate &&
            other.isWatched === this.isWatched
        );
    }
    toDOM(): HTMLElement {
        const icon = createSyntaxBadge("F", this.isTemplate ? { sub: "T", template: true } : undefined);
        icon.classList.add("cm-magic-for-pretty-icon");

        const wrapper = createPrettySyntaxShell({
            wrapperClass: "cm-magic-for-pretty",
            badge: icon,
            primary: [
                createTextSpan("cm-magic-for-pretty-id", this.forId),
                createTextSpan("cm-magic-for-pretty-var", `$${this.each}`),
                createTextSpan("cm-magic-for-pretty-in", "of"),
                createForSourceSpan(this.ofExpr),
                ...(this.isTemplate
                    ? [createTextSpan("cm-magic-for-pretty-template", "template")]
                    : []),
                ...(this.isWatched ? [createWatchIndicator(this.forId)] : []),
            ],
            detail: this.summary
                ? createHoverDetail("cm-magic-for-pretty-detail", [
                    this.isTemplate
                        ? createTextSpan("cm-magic-hover-kind cm-magic-for-pretty-kind", "for template")
                        : null,
                    createTextSpan("cm-magic-hover-summary cm-magic-for-pretty-summary", this.summary),
                ])
                : null,
        });
        attachSyntaxEditInteractions(wrapper);
        return wrapper;
    }
    ignoreEvent(): boolean {
        return true;
    }
}

function createForSourceSpan(ofExpr: string): HTMLElement {
    const refTarget = parseRefTarget(ofExpr);
    if (!refTarget) {
        return createTextSpan("cm-magic-for-pretty-source", ofExpr || "items");
    }

    const wrapper = document.createElement("span");
    wrapper.className = "cm-magic-for-pretty-source cm-magic-for-pretty-source-ref";
    wrapper.dataset.magicNavTarget = refTarget.targetId;
    wrapper.dataset.magicNavKind = "for-of";
    wrapper.title = ofExpr;

    const badge = createSyntaxBadge("r");
    badge.classList.add("cm-magic-for-pretty-ref-icon");
    wrapper.appendChild(badge);
    wrapper.appendChild(createTextSpan("cm-magic-for-pretty-ref-id", refTarget.targetId));
    if (refTarget.path) {
        wrapper.appendChild(createTextSpan("cm-magic-for-pretty-ref-path", `›${refTarget.path}`));
    }

    return wrapper;
}

function parseRefTarget(ofExpr: string): { targetId: string; path: string } | null {
    const match = ofExpr.trim().match(/^\$ref\{([^}]+)\}$/);
    const refContent = match?.[1]?.trim();
    if (!refContent || refContent.startsWith("$") || refContent.startsWith("input/") || parseAssetRefPath(refContent)) {
        return null;
    }

    const dotIdx = refContent.indexOf(".");
    if (dotIdx <= 0) return null;

    return {
        targetId: refContent.slice(0, dotIdx),
        path: refContent.slice(dotIdx + 1),
    };
}

/* ── ForButtonGroupWidget — Run/Stop button for for-block header, matching define's style ── */

export class ForButtonGroupWidget extends WidgetType {
    constructor(
        readonly forId: string,
        readonly status: ForExecutionStatus,
        readonly canRun: boolean = true,
        readonly canInlineRender: boolean = true,
        readonly inlineRendered: boolean = false,
        readonly isTemplate: boolean = false,
    ) {
        super();
    }
    eq(other: ForButtonGroupWidget) {
        return (
            other.forId === this.forId &&
            other.status === this.status &&
            other.canRun === this.canRun &&
            other.canInlineRender === this.canInlineRender &&
            other.inlineRendered === this.inlineRendered &&
            other.isTemplate === this.isTemplate
        );
    }
    toDOM(): HTMLElement {
        const group = document.createElement("span");
        group.className = "cm-magic-btn-group";

        // Prevent ALL mouse events on the group from reaching CodeMirror,
        // so clicks here never trigger fold/unfold or cursor placement.
        shieldGroupEvents(group);

        const isRunning = this.status === "running";
        const forId = this.forId;

        // Run/Stop button
        const runBtn = document.createElement("span");
        runBtn.className = isRunning
            ? "cm-magic-btn cm-magic-btn-stop"
            : "cm-magic-btn cm-magic-btn-run";
        runBtn.textContent = isRunning ? "■" : "▶";
        runBtn.title = isRunning
            ? tr("syntax.widget.for.stop", { id: this.forId })
            : tr("syntax.widget.for.run", { id: this.forId });
        runBtn.setAttribute(
            "aria-label",
            runBtn.title,
        );
        runBtn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isRunning) {
                window.dispatchEvent(
                    new CustomEvent("magic-md:cancel-for", {
                        detail: { forId },
                    }),
                );
            } else {
                window.dispatchEvent(
                    new CustomEvent("magic-md:run-for", {
                        detail: { forId },
                    }),
                );
            }
        });

        if (this.canRun && !this.isTemplate) {
            group.appendChild(runBtn);
        }

        if (this.isTemplate) {
            group.appendChild(
                createTemplateInputsButton(
                    "for",
                    forId,
                    tr("syntax.widget.for.editInputs", { id: this.forId }),
                ),
            );
        }

        // Inline render toggle
        if (this.canInlineRender && !this.isTemplate) {
            group.appendChild(
                createInlineRenderToggleButton(
                    forId,
                    this.inlineRendered,
                    this.inlineRendered
                        ? tr("syntax.widget.for.hideInlineRender", { id: this.forId })
                        : tr("syntax.widget.for.showInlineRender", { id: this.forId }),
                ),
            );
        }

        if (this.status === "error") {
            group.appendChild(
                createClearExecutionButton(
                    forId,
                    tr("syntax.widget.for.clearError", { id: this.forId }),
                    tr("syntax.widget.for.clearError", { id: this.forId }),
                ),
            );
        }

        return group;
    }
    ignoreEvent(): boolean {
        return true;
    }
}

/* ── ForEndWidget — L0 pretty widget for ::endfor ── */

export class ForEndWidget extends WidgetType {
    eq(_other: ForEndWidget) {
        return true;
    }
    toDOM(): HTMLElement {
        const span = document.createElement("span");
        span.className = "cm-magic-for-end-pretty";
        span.textContent = "⤶ endfor";
        attachSyntaxEditInteractions(span);
        return span;
    }
    ignoreEvent(): boolean {
        return true;
    }
}
